import { JSX, useCallback, useMemo, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { BrandHealthEntry } from '@blisspointmedia/bpm-types/dist/BrandHealthMetrics';
import { Button } from '../../Components/Button';
import { ChartContainer } from '../../Components/ChartContainer';
import { Design, Size } from '../../Components/designConstants';
import { DownloadDropdown } from '../../Components/DownloadDropdown/DownloadDropdown';
import { GraphTableToggleButton } from '../../Components/GraphTableToggleButton/GraphTableToggleButton';
import { InfoTooltip } from '../../Components/InfoTooltip';
import { Spinner } from '../../Components/Spinner/Spinner';
import { useAppSelector } from '../../redux';
import { isInternalSelector } from '../../redux/user';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { Brand60, Brand100, Neutral300 } from '../../utils/colors';
import { DateRange } from '../../utils/date-utils';
import { downloadPNG, exportToExcel } from '../../utils/download-utils';
import './BrandMetricsContent.scss';
import { Key } from './Key';

export const formatDate = (date: string): string => {
  const splitDate = date.split('-');
  const year = splitDate[0];
  const month = parseInt(splitDate[1], 10);
  const day = parseInt(splitDate[2], 10);

  return [month, day, year.substring(2)].join('/');
};

// Zero and missing values are rendered as gaps rather than dips to zero, so the line series omits them.
const toLineValue = (value: string | number): number | null => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

interface BrandMetricsContentProps {
  focalAdvertiser: string;
  nonFocalAdvertisers: string[];
  type: 'Awareness' | 'Consideration';
  infoBarText: string;
  toolTipText: string;
  data: BrandHealthEntry[];
  dateRange: DateRange;
}

export const BrandMetricsContent = ({
  focalAdvertiser,
  nonFocalAdvertisers,
  type,
  infoBarText,
  toolTipText,
  data,
  dateRange
}: BrandMetricsContentProps) => {
  const { cid } = useCompanyInfo();
  const isInternal = useAppSelector(isInternalSelector);
  const ref = useRef<SVGSVGElement | null>(null);
  const [graphView, setGraphView] = useState(true);
  // Funnel-stage ('Awareness' | 'Consideration') lowercased for use as a CSS class.
  const typeClass = type.toLowerCase();

  const filteredData: BrandHealthEntry[] = useMemo(
    () =>
      data.filter(
        entry =>
          Date.parse(entry.date) >= Date.parse(dateRange.start) &&
          Date.parse(entry.date) <= Date.parse(dateRange.end) &&
          entry.metric === type
      ),
    [data, dateRange.end, dateRange.start, type]
  );

  const chartData = useMemo(
    () =>
      filteredData.map(entry => ({
        ...entry,
        focal_ma_line: toLineValue(entry.focal_ma),
        non_focal_ma_line: toLineValue(entry.non_focal_ma)
      })),
    [filteredData]
  );

  const { dataMin, dataMax } = useMemo(() => {
    let minVal = 100;
    let maxVal = 0;
    chartData.forEach(entry => {
      if (entry.focal_ma_line !== null) {
        minVal = Math.min(minVal, entry.focal_ma_line);
        maxVal = Math.max(maxVal, entry.focal_ma_line);
      }
      if (entry.non_focal_ma_line !== null) {
        minVal = Math.min(minVal, entry.non_focal_ma_line);
        maxVal = Math.max(maxVal, entry.non_focal_ma_line);
      }
    });
    return { dataMin: minVal, dataMax: maxVal };
  }, [chartData]);

  const tableFormattedData: {
    date: string;
    row1DataEntry: string;
    row2DataEntry: string;
    row3DataEntry: string;
    row4DataEntry: string;
  }[] = useMemo(
    () =>
      filteredData.map(entry => {
        const updatedFocalMa = `${entry.focal_ma}%`;
        const updatedNonFocalMa = `${entry.non_focal_ma}%`;
        return {
          date: formatDate(entry.date),
          row1DataEntry: updatedFocalMa,
          row2DataEntry: entry.focal_sample_size,
          row3DataEntry: updatedNonFocalMa,
          row4DataEntry: entry.non_focal_sample_size
        };
      }),
    [filteredData]
  );

  const CustomTooltip: (params: any) => JSX.Element | null = useCallback(
    params => {
      const hasCompetitors = nonFocalAdvertisers.length > 0;
      const { payload } = params;
      if (payload && payload[0]) {
        const { date, focal_ma, focal_sample_size, non_focal_ma, non_focal_sample_size } =
          payload[0].payload;
        return (
          <div className='bmc-graph-tooltip'>
            <div className='bmc-date'>{formatDate(date)}</div>
            {hasCompetitors && <div className='bmc-focal-advertiser'>{focalAdvertiser}</div>}
            <div className='bmc-focal-ma'>
              <div className='label'>{`${type}:`}&nbsp;</div>
              <div className='value'>{`${focal_ma}%`}</div>
            </div>
            <div className='bmc-focal-sample-size'>
              <div className='label'>Sample Size:&nbsp;</div>
              <div className='value'>{focal_sample_size}</div>
            </div>
            {hasCompetitors && (
              <div className='bmc-competitor'>
                {nonFocalAdvertisers.length === 1 ? 'Competitor' : 'Competitors'}
              </div>
            )}
            {hasCompetitors && (
              <div className='bmc-non-focal-ma'>
                <div className='label'>{`${type}:`}&nbsp;</div>
                <div className='value'>{`${non_focal_ma}%`}</div>
              </div>
            )}
            {hasCompetitors && (
              <div className='bmc-non-focal-sample-size'>
                <div className='label'>Sample Size:&nbsp;</div>
                <div className='value'>{`${non_focal_sample_size}`}</div>
              </div>
            )}
          </div>
        );
      }

      return null;
    },
    [focalAdvertiser, nonFocalAdvertisers.length, type]
  );

  const MetricsGraph: JSX.Element = useMemo(
    () => (
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 5,
            bottom: -12,
            left: -25
          }}
          ref={ref}
        >
          {/* Recharts stroke colors below have no exact palette equivalent; left as literals */}
          <CartesianGrid strokeWidth='1px' stroke={Neutral300} vertical={false} />
          <XAxis
            dataKey='date'
            axisLine={{ stroke: '#4e4e50', strokeWidth: '1px' }}
            tickFormatter={value => formatDate(value)}
            tick={{ stroke: Brand100, strokeWidth: '0.15px' }}
          />
          <YAxis
            tickFormatter={value => `${value}%`}
            axisLine={{ stroke: '#4e4e50', strokeWidth: '1px' }}
            tick={{ stroke: Brand100, strokeWidth: '0.15px' }}
            domain={[Math.floor(dataMin), Math.ceil(dataMax)]}
          />
          <Tooltip content={CustomTooltip} cursor={{ stroke: Neutral300 }} />
          <Line
            type='monotone'
            dataKey='focal_ma_line'
            stroke={Brand60}
            dot={false}
            strokeWidth='2px'
          />
          {nonFocalAdvertisers.length > 0 && (
            <Line
              type='monotone'
              dataKey='non_focal_ma_line'
              stroke='#717171'
              dot={false}
              strokeWidth='2px'
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    ),
    [CustomTooltip, dataMax, dataMin, chartData, nonFocalAdvertisers.length]
  );

  const [tableOrientation, setTableOrientation] = useState<'Horizontal' | 'Vertical'>('Horizontal');

  const tableGrid = useMemo(() => {
    const hasCompetitors = nonFocalAdvertisers.length > 0;
    const headers = hasCompetitors
      ? [
          'Date',
          `${focalAdvertiser} ${type}`,
          `${focalAdvertiser} Sample Size`,
          `Competitor ${type}`,
          'Competitor Sample Size'
        ]
      : ['Date', type, 'Sample Size'];
    const rows = tableFormattedData.map(entry =>
      hasCompetitors
        ? [
            entry.date,
            entry.row1DataEntry,
            entry.row2DataEntry,
            entry.row3DataEntry,
            entry.row4DataEntry
          ]
        : [entry.date, entry.row1DataEntry, entry.row2DataEntry]
    );
    const grid = [headers, ...rows];
    // Matches bpm-frontend: 'Horizontal' (the default) shows dates as columns
    // (metrics as rows), 'Vertical' shows dates as rows.
    if (tableOrientation === 'Vertical') {
      return grid;
    }
    return grid[0].map((_, colIndex) => grid.map(row => row[colIndex]));
  }, [focalAdvertiser, nonFocalAdvertisers.length, tableFormattedData, tableOrientation, type]);

  const handleExportToExcel = useCallback(() => {
    const focalMaHeader = `${focalAdvertiser} ${type}`;
    const focalSampleSizeHeader = `${focalAdvertiser} Sample Size`;
    const nonFocalMaHeader = `Competitor ${type}`;
    const nonFocalSampleSizeHeader = 'Competitor Sample Size';
    const hasCompetitors = nonFocalAdvertisers.length > 0;

    const excelData = filteredData.map(entry => {
      const { date, focal_ma, focal_sample_size, non_focal_ma, non_focal_sample_size } = entry;
      return {
        Date: formatDate(date),
        [focalMaHeader]: parseFloat(String(focal_ma)) / 100,
        [focalSampleSizeHeader]: Number(focal_sample_size.replace(',', '')),
        ...(hasCompetitors && {
          [nonFocalMaHeader]: parseFloat(String(non_focal_ma)) / 100,
          [nonFocalSampleSizeHeader]: Number(non_focal_sample_size.replace(',', ''))
        })
      };
    });

    exportToExcel(excelData, `${cid}_BrandHealth_${type}`, {
      Date: 'm/d/yy',
      [focalMaHeader]: '0.000',
      [focalSampleSizeHeader]: '#,###',
      ...(hasCompetitors && {
        [nonFocalMaHeader]: '0.000',
        [nonFocalSampleSizeHeader]: '#,###'
      })
    });
  }, [cid, filteredData, focalAdvertiser, nonFocalAdvertisers.length, type]);

  const handleDownloadPNG = useCallback(
    () => downloadPNG(`.show-graph-${typeClass}`, `${cid}_BrandHealth_${type}`),
    [cid, type, typeClass]
  );

  return (
    <ChartContainer
      title={`Brand ${type}`}
      viewRightActions={filteredData.length > 0}
      rightActions={
        filteredData.length > 0 && (
          <>
            {!graphView && (
              <Button
                design={Design.NEUTRAL}
                size={Size.SM}
                onClick={() =>
                  setTableOrientation(tableOrientation === 'Horizontal' ? 'Vertical' : 'Horizontal')
                }
              >
                Swap Table Axes
              </Button>
            )}
            <GraphTableToggleButton
              graphView={graphView}
              setGraphView={setGraphView}
              widgetName='brand_metrics_content'
            />
            {isInternal && (
              <DownloadDropdown
                size={Size.SM}
                onClickOptions={[handleExportToExcel, handleDownloadPNG]}
                disabledMenuOptions={
                  graphView ? { XLSX: false, PNG: false } : { XLSX: false, PNG: true }
                }
              />
            )}
          </>
        )
      }
    >
      <div
        className={`bmc-content-container ${graphView ? 'show-graph' : 'show-table'}-${typeClass}`}
      >
        <div className='bmc-content'>
          <div className='bmc-info-section'>
            <div className='bmc-label'>{type}</div>
            <div className='bmc-info-bar'>
              <div className='bmc-text'>{infoBarText}</div>
              <div className='bmc-tooltip-icon'>
                <InfoTooltip size={Size.SM}>{toolTipText}</InfoTooltip>
              </div>
            </div>
          </div>
          {filteredData.length > 0 && graphView && (
            <div className='bmc-data'>
              <div className='bmc-graph-section'>{MetricsGraph}</div>
              <div className='bmc-legend'>
                <Key
                  focalAdvertiser={focalAdvertiser}
                  nonFocalAdvertisers={nonFocalAdvertisers}
                  type={type}
                />
              </div>
            </div>
          )}
          {filteredData.length > 0 && !graphView && (
            <div className='bmc-data show-table'>
              <Table size='sm'>
                <tbody>
                  {tableGrid.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) =>
                        rowIndex === 0 || (tableOrientation !== 'Vertical' && cellIndex === 0) ? (
                          <th key={cellIndex}>{cell}</th>
                        ) : (
                          <td key={cellIndex}>{cell}</td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {filteredData.length === 0 && <Spinner />}
        </div>
      </div>
    </ChartContainer>
  );
};
